import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Input,
  Button,
  Spin,
  Col,
  Modal,
  Typography,
  message,
} from 'antd';
import { onError } from 'utils/handlers';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import { deleteAdministrador, getAdministradores, postAdministrador } from 'api/usuarios/administradores';
import { isAdmin } from 'utils/permission';

const { Text } = Typography;

const Administrators = ({
  disabled,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    const res = await getAdministradores();
    setData(res.data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      const _administradores = await getAdministradores();
      if (mounted) {
        setData(_administradores);
        setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
    setSelectedRowKeys([]);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      if (!data.find((d) => d.email === values.email)) {
        const added = await postAdministrador(values);
        if (added) {
          onCancel();
          await fetchData();
        }
      } else {
        message.info('El Usuario ya es administrador de la entidad');
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await deleteAdministrador(key);
    if (deleted) {
      setVisibleAlert(false);
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const rules = {
    email: [{ type: 'email' }],
  };

  const columns = [
    {
      titleText: 'Correo',
      key: 'email',
      dataIndex: 'email',
      width: 300,
    },
    {
      titleText: 'Nombre',
      key: 'first_name',
      dataIndex: 'first_name',
      width: 150,
    },
    {
      titleText: 'Apellido',
      key: 'last_name',
      dataIndex: 'last_name',
      width: 150,
    },
  ];

  return (
    <Spin tip="Cargando..." spinning={loading}>
      <Table
        cols={columns}
        data={data}
        rowSelection={rowSelection}
        handleOnRowClick={handleOnRowClick}
        controls={{
          onClickAdd,
          onClickDelete,
        }}
        allowSort={false}
        allowSearch={false}
        disabled={disabled}
        rowKey="id"
        pageSize={5}
        hasAllViewPermission
        permission={{ add: isAdmin(), delete: isAdmin() }}
      />
      <Modal
        visible={visible}
        closable={false}
        footer={null}
        title={(
          <FormSubmitControls
            label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Administrador`}
            onFinish={onFinish}
            onCancel={onCancel}
            loading={loading}
          />
        )}
        keyboard={!loading}
        maskClosable={!loading}
        onCancel={onCancel}
        forceRender
      >
        <Col span={24}>
          <Form
            form={form}
            layout="vertical"
            onFinish={() => onFinish()}
          >
            <Col span={24}>
              <Form.Item
                name="email"
                label="Correo"
                rules={rules.email}
              >
                <Input allowClear />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Col>
      </Modal>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => {
          setVisibleAlert(false);
          setSelectedRowKeys([]);
        }}
        visible={visibleAlert}
        content={(
          <Text style={{ fontSize: 16 }}>
            {`¿Deseas que ${form.getFieldValue('email')}
              deje de ser administrador de la entidad ?`}
          </Text>
        )}
        loading={loading}
      />
    </Spin>
  );
};

Administrators.propTypes = {
  disabled: PropTypes.bool,
};

Administrators.defaultProps = {
  disabled: false,
};

export default Administrators;
